"use client";

import { AnalyticsBrowser, MiddlewareFunction } from "@segment/analytics-next";

import { logger } from "@/core/logging";
import { EMBED_TOKEN_PARAM } from "@/features/embedding/embed.utils";

import type { ConsentState } from "@/core/consent";

import type { AnalyticsEventMap, AnalyticsTraits } from "@/core/analytics/analytics.types";

// Query params that must never reach analytics destinations. The embed session token
// (a short-lived JWT) rides in the URL for cookie-blocking browsers, so Segment's
// auto-captured page context would otherwise ship it to Segment and every downstream
// destination. Uses the shared constant so a rename of the param cannot silently
// detach this denylist from the URLs the embed flow actually produces.
// "token" is the assessment bearer token param consumed by AssessmentSessionProvider
// (results share links, texted consult-capture links) — same JWT-in-URL exposure.
const SENSITIVE_ANALYTICS_QUERY_PARAMS: readonly string[] = [EMBED_TOKEN_PARAM, "token"];

// analytics-next attaches `context.page` — url/search/referrer/title read from
// window.location and document.referrer — to EVERY event type (page, track, and
// identify alike), and mirrors those fields into page-event properties. These are
// the URL-shaped fields the sanitizer must cover.
const PAGE_URL_FIELDS = ["url", "search", "referrer"] as const;

function containsSensitiveParam(value: string): boolean {
  return SENSITIVE_ANALYTICS_QUERY_PARAMS.some((param) => value.includes(param));
}

/**
 * Strips the sensitive query params from an absolute-URL or `?search`-shaped string.
 * Fails CLOSED: a value that cannot be parsed is replaced with an empty string rather
 * than shipped unsanitized.
 */
function stripSensitiveParams(value: string): string {
  try {
    if (value.startsWith("?") || !value.includes("://")) {
      const params = new URLSearchParams(value.startsWith("?") ? value.slice(1) : value);
      for (const param of SENSITIVE_ANALYTICS_QUERY_PARAMS) {
        params.delete(param);
      }
      const serialized = params.toString();
      return serialized ? `?${serialized}` : "";
    }

    const url = new URL(value);
    for (const param of SENSITIVE_ANALYTICS_QUERY_PARAMS) {
      url.searchParams.delete(param);
    }
    return url.toString();
  } catch {
    return "";
  }
}

function scrubPageUrlFields(holder: Record<string, unknown> | undefined): void {
  if (!holder) {
    return;
  }
  for (const field of PAGE_URL_FIELDS) {
    const value = holder[field];
    if (typeof value === "string" && value && containsSensitiveParam(value)) {
      holder[field] = stripSensitiveParams(value);
    }
  }
}

/**
 * Source middleware that scrubs sensitive query params out of every outgoing event's
 * auto-captured page context before it leaves the browser. Registered once at load so
 * page/track/identify — and any call added later — are all covered in one place; a
 * per-call override inside trackPage would leave the other call types shipping the
 * raw URL (Segment fills `context.page` for all of them). `Referrer-Policy:
 * no-referrer` cannot close this channel: the library reads `location` /
 * `document.referrer` directly via JS.
 *
 * Fails CLOSED: if sanitization itself throws, the event is dropped (`next(null)`)
 * and logged, never shipped unsanitized.
 */
const sanitizeEventPageContext: MiddlewareFunction = ({ payload, next }) => {
  try {
    const event = payload.obj;
    scrubPageUrlFields((event.context as { page?: Record<string, unknown> } | undefined)?.page);
    if (event.type === "page") {
      scrubPageUrlFields(event.properties as Record<string, unknown> | undefined);
    }
    next(payload);
  } catch (e) {
    logger.error("segment_page_context_sanitize_failed", undefined, e as Error);
    next(null);
  }
};

/**
 * The page's Segment SDK, held at module level: the vendor module the segment
 * sink drives, in the shape of `monitoring.ts` (Datadog) and
 * `growthbook.client.ts` (GrowthBook). No interface and no service in between —
 * there is one Segment, and everything Segment-specific lives here and nowhere
 * else: deferred load, the page-context sanitizer, consent-context stamping,
 * ad-blocker-safe wrappers, identity.
 *
 * Nothing loads at import. `loadSegment` is the one entry point: the segment
 * sink calls it once analytics consent activates the sink. (Until the flip,
 * production calls it at module scope from `AnalyticsProvider`, exactly as the
 * legacy adapter's constructor did.)
 */
let segment: AnalyticsBrowser | undefined;

let consentState: ConsentState = {
  analytics: false,
  advertising: false,
  dataSharing: false,
  functional: false,
};

/**
 * Load the Segment SDK. Idempotent: a second call is a no-op, so a sink
 * re-activated after a revoke does not stack a second `AnalyticsBrowser`.
 * A no-op without a write key, which is logged.
 */
export const loadSegment = (): void => {
  if (segment) {
    return;
  }
  const writeKey = process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY;
  if (!writeKey) {
    logger.warn("segment_write_key_missing");
    return;
  }

  try {
    segment = AnalyticsBrowser.load({ writeKey });
    // Buffered by analytics-next until the SDK finishes loading, so the sanitizer
    // is in place before any queued event is dispatched.
    segment.addSourceMiddleware(sanitizeEventPageContext).catch((e: unknown) => {
      logger.error("segment_add_sanitizer_failed", undefined, e as Error);
    });
    logger.warn("segment_initialized");
  } catch (e) {
    logger.error("segment_init_failed", undefined, e as Error);
    segment = undefined;
  }
};

export const setSegmentConsentState = (state: ConsentState): void => {
  logger.info("analytics_consent_state_set", state);
  consentState = state;
};

/**
 * Every event carries the visitor's decision as Segment's own
 * `categoryPreferences`, which is how Segment's consent management filters
 * destinations downstream.
 */
const buildConsentContext = () => ({
  consent: {
    categoryPreferences: {
      Advertising: consentState.advertising,
      Analytics: consentState.analytics,
      Functional: consentState.functional,
      DataSharing: consentState.dataSharing,
    },
  },
});

export const trackSegmentEvent = <Event extends keyof AnalyticsEventMap>(
  event: Event,
  properties: AnalyticsEventMap[Event],
  /** When an event held until the visitor answered was published; it keeps that time. */
  capturedAt?: number,
): void => {
  logger.info("analytics_event_tracked", { eventName: String(event) || null });
  const timestamp = capturedAt === undefined ? {} : { timestamp: new Date(capturedAt) };
  try {
    segment
      ?.track(event, properties, { context: buildConsentContext(), ...timestamp })
      .catch((e: unknown) => {
        logger.error("segment_track_event_failed", undefined, e as Error);
      });
  } catch (e) {
    logger.error("segment_track_event_failed", undefined, e as Error);
  }
};

export const trackSegmentPage = (
  name: string,
  /** When a page view held until the visitor answered was published; it keeps that time. */
  capturedAt?: number,
): void => {
  logger.info("analytics_page_tracked", { pageName: name || null });
  const timestamp = capturedAt === undefined ? {} : { timestamp: new Date(capturedAt) };
  try {
    // `page` resolves its arguments by SHAPE, not position: with a single
    // plain object anywhere in the call it becomes the properties and options
    // are empty — so `page(name, undefined, { context })` shipped the consent
    // context as page properties and Segment's consent management never saw
    // it. Two plain objects resolve as (properties, options).
    segment
      ?.page(name, {}, { context: buildConsentContext(), ...timestamp })
      .catch((e: unknown) => {
        logger.error("segment_track_page_failed", undefined, e as Error);
      });
  } catch (e) {
    logger.error("segment_track_page_failed", undefined, e as Error);
  }
};

export const identifySegmentUser = (userId: string, traits: AnalyticsTraits): void => {
  logger.info("analytics_user_identified", { userId: userId || null });
  try {
    segment?.identify(userId, traits, { context: buildConsentContext() }).catch((e: unknown) => {
      logger.error("segment_identify_failed", undefined, e as Error);
    });
  } catch (e) {
    logger.error("segment_identify_failed", undefined, e as Error);
  }
};

/** Clear Segment's identity (user id, anonymous id, traits). */
export const resetSegment = (): void => {
  logger.info("analytics_reset");
  try {
    segment?.reset();
  } catch (e) {
    logger.error("segment_reset_failed", undefined, e as Error);
  }
};

/**
 * Make Segment's anonymous identity the app's own (the first-party visitor
 * id), so events land under the id that assessments and leads are keyed on.
 * Buffered by analytics-next until the SDK finishes loading; a no-op before
 * `loadSegment`, since there is no identity to set on an unloaded SDK.
 */
export const setSegmentAnonymousId = (id: string): void => {
  try {
    void segment?.setAnonymousId(id);
  } catch (e) {
    logger.error("segment_set_anonymous_id_failed", undefined, e as Error);
  }
};

/** Test-only: forget the SDK and the stamped decision, as on a fresh page. */
export const resetSegmentClientForTests = (): void => {
  segment = undefined;
  consentState = { analytics: false, advertising: false, dataSharing: false, functional: false };
};
